import { SiteAvailabilityControl } from 'src/net-availability/enums/site-availability-control';
import {
  AssetLevelUndeclaredRatingPropertiesError,
  AssetLevelUndeclaredRatingPropertyError,
  AssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError,
  getLimitsAndDeclaredAvailabilitiesGroupedByDateInterval,
  INetAvailabilityError,
} from 'src/net-availability/net-availability';
import {
  AssetDeclaredAvailability,
  AssetLevelNetAvailability,
  AssetLimitForNetAvailability,
  IPowerAndEnergyRating,
} from 'src/net-availability/types';
import { checkAssetNamesAndAssetLimitsAndAssetDeclaredAvailabilities } from 'src/net-availability/utils/asset-limit';
import {
  getAggregatedPowerAndEnergyRating,
  getMinPowerAndEnergyRating,
} from 'src/net-availability/utils/power-and-energy-rating';
import { Result } from 'src/types/result';
import { getMapFrom } from 'src/utils/map';
import { traverseAllResults } from 'src/utils/result/utils';
import {
  DateInterval,
  getGroupedByContinuousDateIntervals,
} from 'src/utils/time-interval';

export const getAssetLimitAsPowerAndEnergyRating = (
  assetLimit: AssetLimitForNetAvailability,
): IPowerAndEnergyRating => {
  const nameplatePowerAndEnergyRating: IPowerAndEnergyRating = {
    maxImportPower: assetLimit.nameplateMaxImportPower,
    maxExportPower: assetLimit.nameplateMaxExportPower,
    energyCapacity: assetLimit.nameplateEnergyCapacity,
  };

  return nameplatePowerAndEnergyRating;
};

export const getAssetLevelNetAvailabilities = ({
  assetNames,
  assetLimits,
  assetDeclaredAvailabilities,
  timeHorizon,
  additionalTimestamps,
}: {
  assetNames: string[];
  assetLimits: AssetLimitForNetAvailability[];
  assetDeclaredAvailabilities: AssetDeclaredAvailability[];
  timeHorizon: DateInterval;
  additionalTimestamps?: Parameters<
    typeof getGroupedByContinuousDateIntervals
  >[0]['additionalTimestamps'];
}): Result<INetAvailabilityError[], AssetLevelNetAvailability[]> => {
  const activeAssetDeclaredAvailabilities = assetDeclaredAvailabilities.filter(
    (item) => !item.isCancelled,
  );

  const activeLimitsAndDeclaredAvailabilitiesGroupedByDateInterval =
    getLimitsAndDeclaredAvailabilitiesGroupedByDateInterval({
      siteAvailabilityControl: SiteAvailabilityControl.AssetLevel,
      limits: assetLimits,
      declaredAvailabilities: activeAssetDeclaredAvailabilities,
      timeHorizon,
      additionalTimestamps,
    });

  const netAvailabilityResults: Result<
    INetAvailabilityError,
    AssetLevelNetAvailability
  >[] = activeLimitsAndDeclaredAvailabilitiesGroupedByDateInterval.map(
    ({
      start,
      end,
      limits: limitsForInterval,
      declaredAvailabilities: activeDeclaredAvailabilitiesForInterval,
    }) =>
      checkAssetNamesAndAssetLimitsAndAssetDeclaredAvailabilities({
        assetNames,
        assetLimits: limitsForInterval,
        assetDeclaredAvailabilities: activeDeclaredAvailabilitiesForInterval,
      })
        .mapLeft(
          (cause) =>
            new AssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError(
              cause,
              {
                start,
                end,
                timeHorizon,
                assetNames,
                assetLimitsForInterval: limitsForInterval,
                rawAssetLimits: assetLimits,
              },
            ),
        )
        .flatMap(
          ({
            assetLimitByAssetName: limitForIntervalByAssetName,
            assetDeclaredAvailabilitiesByAssetName:
              activeDeclaredAvailabilitiesForIntervalByAssetName,
          }) => {
            const minPowerAndEnergyRatingForIntervalForAssetsResult =
              Array.from(limitForIntervalByAssetName.entries()).map(
                ([assetName, limitForIntervalForAsset]) => {
                  const limitAsPowerAndEnergyRatingForIntervalForAsset =
                    getAssetLimitAsPowerAndEnergyRating(
                      limitForIntervalForAsset,
                    );

                  const activeDeclaredAvailabilitiesForIntervalForAsset =
                    activeDeclaredAvailabilitiesForIntervalByAssetName.get(
                      assetName,
                    ) ?? [];

                  const minPowerAndEnergyRatingForIntervalForAssetResult =
                    getMinPowerAndEnergyRating([
                      limitAsPowerAndEnergyRatingForIntervalForAsset,
                      ...activeDeclaredAvailabilitiesForIntervalForAsset,
                    ])
                      .mapLeft(
                        (cause) =>
                          new AssetLevelUndeclaredRatingPropertyError(cause, {
                            start,
                            end,
                            timeHorizon,
                            assetLimitsForInterval: limitsForInterval,
                            rawAssetLimits: assetLimits,
                            assetName,
                            limitAsPowerAndEnergyRatingForIntervalForAsset,
                            activeDeclaredAvailabilitiesForIntervalForAsset,
                            rawAssetDeclaredAvailabilities:
                              assetDeclaredAvailabilities,
                          }),
                      )
                      .map((minPowerAndEnergyRating) => ({
                        assetName,
                        minPowerAndEnergyRating,
                      }));

                  return minPowerAndEnergyRatingForIntervalForAssetResult;
                },
              );

            const minPowerAndEnergyRatingForIntervalByAssetResult =
              traverseAllResults(
                minPowerAndEnergyRatingForIntervalForAssetsResult,
              )
                .mapLeft(
                  (cause) =>
                    new AssetLevelUndeclaredRatingPropertiesError(
                      cause.map((it) => it.details),
                    ),
                )
                .map((minPowerAndEnergyRatingForIntervalForAssets) =>
                  getMapFrom(minPowerAndEnergyRatingForIntervalForAssets)
                    .withKeyFrom('assetName')
                    .andValueFrom('minPowerAndEnergyRating'),
                );

            // if successful, map the PowerAndEnergyRating to a NetAvailability
            return minPowerAndEnergyRatingForIntervalByAssetResult.map(
              (minPowerAndEnergyRatingForIntervalByAsset) => {
                const netAvailability: AssetLevelNetAvailability = {
                  siteAvailabilityControl: SiteAvailabilityControl.AssetLevel,
                  assetLimits: limitsForInterval,
                  assetActiveDeclaredAvailabilities:
                    activeDeclaredAvailabilitiesForInterval,
                  start,
                  end,
                  netPowerAndEnergyRatings: {
                    site: getAggregatedPowerAndEnergyRating(
                      Array.from(
                        minPowerAndEnergyRatingForIntervalByAsset.values(),
                      ),
                    ),
                    perAsset: minPowerAndEnergyRatingForIntervalByAsset,
                  },
                };

                return netAvailability;
              },
            );
          },
        ),
  );

  return traverseAllResults(netAvailabilityResults);
};
